import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CropFreeIcon from "@material-ui/icons/CropFree";
import { useMutation } from "@apollo/client";
import { Mutation, MutationEnsureInvoiceReviewShortlinkArgs } from "../../generated/nest-graphql";
import { ENSURE_INVOICE_REVIEW_SHORTLINK } from "../../graphql/mutations/ensureInvoiceReviewShortlink";
import { showErrorAlert } from "../../actions";
import { useDispatch } from "../../contexts/snackbar-context";
import { ReviewQrDialog } from "./ReviewQrDialog";

/**
 * Green "Show Review QR" action (NUB-376). Two modes:
 * - invoiceId set (invoice page): tapping fetches the invoice's review shortcode via
 *   ensureInvoiceReviewShortlink, which lazily mints the canonical invoice shortlink for
 *   invoices created before shortlinks existed.
 * - shortcode set (job page): display-only; renders the QR for the code it was given and
 *   never triggers shortlink generation.
 */
export const ShowReviewQrButton: React.FC<{
  invoiceId?: string;
  shortcode?: string;
  /** Overrides the bright default green, e.g. where white text must stay readable. */
  background?: string;
}> = ({ invoiceId, shortcode, background = "#3E6" }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [fetchedShortcode, setFetchedShortcode] = useState<string | null>(null);
  const [ensureShortlink, { loading }] = useMutation<Mutation, MutationEnsureInvoiceReviewShortlinkArgs>(
    ENSURE_INVOICE_REVIEW_SHORTLINK
  );

  const resolvedShortcode = shortcode ?? fetchedShortcode;

  const handleClick = async () => {
    if (resolvedShortcode) {
      setOpen(true);
      return;
    }
    if (!invoiceId) return;
    try {
      const { data } = await ensureShortlink({ variables: { invoiceId } });
      const code = data?.ensureInvoiceReviewShortlink;
      if (!code) {
        showErrorAlert(dispatch, "Could not load the review link for this invoice");
        return;
      }
      setFetchedShortcode(code);
      setOpen(true);
    } catch (e) {
      showErrorAlert(dispatch, "Could not load the review link for this invoice");
    }
  };

  return (
    <>
      <Button
        className="w-1/3 bg-green-400"
        style={{ backgroundColor: background }}
        variant={"contained"}
        color={"inherit"}
        onClick={handleClick}
        disabled={loading}
      >
        <div>
          <CropFreeIcon />
          <Typography display={"block"} variant={"caption"}>
            Show Review QR
          </Typography>
        </div>
      </Button>
      {resolvedShortcode && <ReviewQrDialog shortcode={resolvedShortcode} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};
